import { useResourceNavigate } from "@/hooks/useResourceNavigate";
import { useResourceContext } from "@/layouts/ResourceContext";
import {
  CloseCircleOutlined,
  CloudSyncOutlined,
  DeleteOutlined,
  DownOutlined,
  PlayCircleOutlined,
  PlusCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Modal, Space, Input, notification } from "antd";
import {
  RESOURCE_TYPE_INSTANCES,
  CHANGE_NODEGROUP,
  CHANGE_HADOOP,
  CHANGE_MYSQL,
  CHANGE_VIA,
  CHANGE_DOMAIN_NAME,
  CHANGE_DRUID,
  ENABLE_DRUID,
  DISABLE_DRUID,
  CHANGE_GITEA,
  ENABLE_GITEA,
  DISABLE_GITEA,
  CHANGE_NEO4J,
  ENABLE_NEO4J,
  DISABLE_NEO4J,
} from "./instances";
import { InstanceDetailPanel } from "@/components/instance/panels/Detail";
import { InstanceAuditPanel } from "@/components/instance/panels/Audit";
import { AccessPanel } from "@/components/instance/panels/Access";
import { EventsPanel } from "@/components/instance/panels/Events";
import { InstanceSecurityPanel } from "@/components/instance/panels/Security";
import { InstanceTopologyPanel } from "@/components/instance/panels/Topology";
import { InstanceDataPanel } from "@/components/instance/panels/Data";
import { useTranslation } from "react-i18next";
import { useInstanceUpdate } from "@/hooks/useInstanceUpdate";
import { useEffect, useState, useRef, act } from "react";
import { useInvalidate, CanAccess, useCan, useUpdate } from "@refinedev/core";
import { RefreshButton } from "@/components/common/RefreshButton";
import { InstanceAddonsPanel } from "@/components/instance/panels/Addons";
import { useResourceDelete } from "@/hooks/useResourceDelete";
import { MonitoringPanel } from "@/components/instance/panels/Monitoring";
import { disableInstanceMaintenance, getInstanceStatus, getStreamingLogName } from "@/apis";
import { RESOURCE_TYPE_ADDONS } from "./Addons";
import { io } from "socket.io-client";
import { MaintenancesPanel } from "@/components/instance/panels/Maintenance";

const RESOURCE = RESOURCE_TYPE_INSTANCES;
const { TextArea } = Input;

export const InstanceActions = () => {
  const { t } = useTranslation();
  const { rootState, state, dispatch } = useResourceContext();
  const navigate = useResourceNavigate();
  const { mutate, isLoading: isUpdating } = useInstanceUpdate({
    resource: RESOURCE,
  });

  const [ongoingLog, setOngoingLog] = useState("");
  const [loadingLog, setLoadingLog] = useState(false);
  const key = useRef(Math.random().toString(36).substr(2, 9));
  const messages = useRef([]);
  const socket = useRef(null);
  const currentStreamingId = useRef("");
  const [isOpen, setIsOpen] = useState(false);
  const textRef = useRef(null);

 const onCloseStreaming = () => {
   if(socket.current != null){
     socket.current.disconnect();
     socket.current = null;
     setIsOpen(false);
   }
 };

  const onStreamingLog = async (id, key) => {
     currentStreamingId.current = id;
     const response = await getStreamingLogName(id);
     if(response.status == "OK"){
        socket.current = io('https://cloud.vitriadev.com',{
          "path":"/api/v1/provision/socket.io",
          "query": "log_file=" + response.value + "&key=" + key.current,
          "transports": ["websocket"]
        });
        
        socket.current.on("connect_error", (err) => {
          // the reason of the error, for example "xhr poll error"
          console.log(">>>" + err.message);
        
          // some additional description, for example the status code of the initial HTTP response
          console.log(">>>" + err.description);
        
          // some additional context, for example the XMLHttpRequest object
          console.log(">>>" + err.context);
        });
    
        socket.current.on(key.current, function(msg) {
          if(messages.current.length == 5000){//show latest 5000 lines
            messages.current.shift();
          }
          const textarea = textRef.current.resizableTextArea.textArea;
          let diff = textarea.scrollHeight - textarea.scrollTop - textarea.offsetHeight;
          messages.current.push(msg.message);
          setOngoingLog(messages.current.join("\n"));
          if(diff < 10) {
            setTimeout(() => {
              textarea.scrollTop = textarea.scrollHeight;
            }, 200);        
          }
       });
  
     }
   };

  const { mutate: mutateAddon } = useUpdate({
    resource: RESOURCE_TYPE_ADDONS,
    successNotification: (data1, values, resource) => {
      if(data1.status == "OK") {
        setIsOpen(true);
        messages.current = [];
        setOngoingLog("");
        const id = data1.value.progress_id;
        if(currentStreamingId.current != id){
          key.current = Math.random().toString(36).substr(2, 9);    
        }
        onStreamingLog(id, key);
      }
    },
    errorNotification: (data1, values, resource) => {
      
    },
  });

  
  const { mutateAsync: deleteMutate } = useResourceDelete();
  const invalidate = useInvalidate();
  const [modalOpen, setModalOpen] = useState(false);
  const [actionsMenu, setActionsMenu] = useState([]);
  const instanceState = [
    {
      key: "instanceState",
      label: "Instance States",
      type: "group",
      children: [
        {
          key: "start",
          label: (
            <span>
              <PlayCircleOutlined /> {t("instances.status.start")}
            </span>
          ),
        },
        {
          key: "stop",
          label: (
            <span>
              <StopOutlined /> {t("instances.status.stop")}
            </span>
          ),
        },
        {
          key: "delete",
          label: (
            <span>
              <DeleteOutlined /> {t("instances.status.delete")}
            </span>
          ),
        },
      ],
    }
  ]
  const [lifecycleMenu, setLifecycleMenu] = useState(instanceState);

  const [selectedInstance] = state.selectedResources;

  const { data: tenantMenuAction } = useCan({action:"tenantMenuAction", resource:"instances"});

  const updateMenu = (instanceDetail) => {
    const subMenu =
      selectedInstance?.type === "silo"
        ? [
            {
              key: CHANGE_NODEGROUP,
              label: "Change Node Group",
              disabled: !selectedInstance,
            },
            {
              key: CHANGE_HADOOP,
              label: "Change Hadoop",
              disabled: !selectedInstance,
            },
            {
              key: CHANGE_MYSQL,
              label: "Change MySQL",
              disabled: !selectedInstance,
            },
            {
              label: "Change Druid",
              disabled: !selectedInstance,
              children: [
                {
                  key: ENABLE_DRUID,
                  label: 'Enable Druid',
                },
                {
                  key: CHANGE_DRUID,
                  label: 'Update Druid',
                },
                {
                  key: DISABLE_DRUID,
                  label: 'Disable Druid',
                },
              ],
            },
            {
              label: "Change Gitea",
              disabled: !selectedInstance,
              children: [
                {
                  key: ENABLE_GITEA,
                  label: 'Enable Gitea',
                },
                {
                  key: CHANGE_GITEA,
                  label: 'Update Gitea',
                },
                {
                  key: DISABLE_GITEA,
                  label: 'Disable Gitea',
                },
              ],
            },
            {
              label: "Change Neo4j",
              disabled: !selectedInstance,
              children: [
                {
                  key: ENABLE_NEO4J,
                  label: 'Enable Neo4j',
                },
                {
                  key: CHANGE_NEO4J,
                  label: 'Update Neo4j',
                },
                {
                  key: DISABLE_NEO4J,
                  label: 'Disable Neo4j',
                },
              ],
            },
            {
              key: CHANGE_VIA,
              label: "Change VIA",
              disabled: !selectedInstance,
            },
            {
              key: CHANGE_DOMAIN_NAME,
              label: "Change Domain Name",
              disabled: !selectedInstance,
            },
          ]
        : [
            {
              key: CHANGE_VIA,
              label: "Change VIA",
              disabled: !selectedInstance,
            },
            {
              key: CHANGE_DOMAIN_NAME,
              label: "Change Domain Name",
              disabled: !selectedInstance,
            },
          ];
    const menu = tenantMenuAction?.can ? [
      {
        key: "changeIpWhiteList",
        label: "Change IP White List",
        disabled: !selectedInstance,
      },
      {
        key: "changeUsers",
        label: "Change Users",
        disabled: !selectedInstance,
      }
    ] : [
      {
        key: "settings",
        label: "Instance Settings",
        type: "group",
        children: subMenu,
      },
      {
        key: "security",
        label: "Security",
        type: "group",
        children: [
          {
            key: "changeIpWhiteList",
            label: "Change IP White List",
            disabled: !selectedInstance,
          },
          {
            key: "changeUsers",
            label: "Change Users",
            disabled: !selectedInstance,
          },
          {
            key: "changeBuiltInPasswords",
            label: "Change Built-in Passwords",
            disabled: !selectedInstance,
          },
          { key: "manageToken", label: "Manage Token", disabled: !selectedInstance },
          {
            key: "maintenance",
            label: "Maintenance",
            type: "group",
            children: [
              {
                key: instanceDetail?.maintenance ? "updateMaintenance": "enableMaintenance",
                label: instanceDetail?.maintenance? "Update Maintenance": "Enter to Maintenance Mode",
                disabled: !selectedInstance,
              },
              {
                key: "leaveMaintenance",
                label: "Leave Maintenance Mode",
                disabled: !selectedInstance || !instanceDetail?.maintenance,
              }
            ],
          }
        ],
      }
    ];
    setActionsMenu(menu);
  }

  useEffect(() => {
    const getInstanceDetail = async ( id ) => {
      try {
        const res = await getInstanceStatus(id);
        if(res?.status === "OK") {
          const instanceDetail = res.value;
          if(instanceDetail.modules) {
            const moduleState = {
              key: "moduleState",
              label: "Module States",
              type: "group"
            };
            const children = instanceDetail.modules.map(module => ({
              label: module.name,
              disabled: !selectedInstance,
              children: [
                {
                  key: module.name + "#start",
                  disabled: !selectedInstance,
                  label: (
                    <span>
                      <PlayCircleOutlined /> {t("instances.status.start")}
                    </span>
                  ),
                },
                {
                  key: module.name + "#stop",
                  label: (
                    <span>
                      <StopOutlined /> {t("instances.status.stop")}
                    </span>
                  ),
                },
                {
                  key: module.name + "#purge",
                  label: (
                    <span>
                      <CloseCircleOutlined /> {t("instances.status.purge", "Purge")}
                    </span>
                  ),
                },
                {
                  key: module.name + "#redeploy",
                  label: (
                    <span>
                      < CloudSyncOutlined /> {t("instances.status.redeploy", "Redeploy")}
                    </span>
                  ),
                },
              ],
            }))
            moduleState["children"] = children;
            setLifecycleMenu([...instanceState, moduleState]);
          }
          if(instanceDetail)
            updateMenu(instanceDetail);
        }
      } catch (error) {
        console.error("get instance failed:", error);
      }
    }
    if(selectedInstance?.id) {
      const res = getInstanceDetail(selectedInstance.id);
    }
    // updateMenu();
  }, [selectedInstance]);

  const launchMenu = [
    { key: "silo", label: "Silo (isolated instance)" },
    { key: "pool-infra", label: "Pool (sharing Hadoop/MySQL with others)" },
  ];

  const onClickLifecycle = (item) => {
    const [selectedInstance] = state.selectedResources;
    if (item.key === "delete") {
      deleteMutate({
        resource: RESOURCE,
        id: selectedInstance.id,
      });
    } else if(item.key.indexOf("#") != -1) {
      const key = item.key.split("#")[1];
      const module = item.key.split("#")[0];
      mutateAddon({
        id: selectedInstance.id,
        values: {
          action: key,
          module : module
        }
      });
    } else {
      mutate({
        action: item.key,
        id: selectedInstance.id,
      });
    }
  };
  const onClickAction = (item) => {
    const [selectedInstance] = state.selectedResources;
    const action = item.key;
    if(item.key === DISABLE_DRUID) {
      Modal.confirm({
        title: t("druid.disableConfirm", "Disable Druid"),
        content: t("druid.disableConfirmContent", `This action cannot be undone. Are you sure you want to disable the druid from current instance?`),
        centered: true,
        onOk: () => {
          mutate({
            id: selectedInstance.id,
            action: action
          });
        },
        onCancel: () => {
          console.log("Cancel delete");
        },
      });
    } else if(item.key === DISABLE_GITEA) {
      Modal.confirm({
        title: t("gitea.disableConfirm", "Disable Gitea"),
        content: t("gitea.disableConfirmContent", `The gitea deploy will be scaled down, but the repository volume is retained. Are you sure you want to disable the gitea from current instance?`),
        centered: true,
        onOk: () => {
          mutate({
            id: selectedInstance.id,
            action: action
          });
        },
        onCancel: () => {
          console.log("Cancel disable");
        },
      });
    } else if(item.key === DISABLE_NEO4J) {
      Modal.confirm({
        title: t("neo4j.disableConfirm", "Disable Neo4j"),
        content: t("neo4j.disableConfirmContent", `The neo4j deploy will be scaled down, but the data volume is retained. Are you sure you want to disable neo4j from current instance?`),
        centered: true,
        onOk: () => {
          mutate({
            id: selectedInstance.id,
            action: action
          });
        },
        onCancel: () => {
          console.log("Cancel disable");
        },
      });
    } else if (item.key == "leaveMaintenance") {
      Modal.confirm({
        title: 'Leave Maintenance',
        content: 'Are you sure to leave maintenance for instance "' + selectedInstance.id + '"?' ,
        centered: true,
        onOk: () => {
          disableInstanceMaintenance(selectedInstance.id).then(({ status, value }) => {
            if (status === 'OK') {
              notification.success({
                description: 'Leave maintenance successfully!'
              });
              updateMenu();
            } else {
              notification.error({
                description: 'Leave maintenance failed!'
              });
            }
          }).catch((error) => {
          
          })
        },
        onCancel: () => {
          console.log("Cancel leave");
        },
      });
    } else {
      navigate({
        resource: RESOURCE,
        action: action,
        key: selectedInstance.id,
      });
    }
  };
  const onClickLaunch = (item) => {
    navigate(
      { resource: RESOURCE, action: "create" },
      { state: { key: item.key, resource: RESOURCE } },
    );
  };

  const buttons = [
    <CanAccess key="menu-state" resource="instances" action="menuState">
      <Dropdown
        menu={{ items: lifecycleMenu, onClick: onClickLifecycle }}
        placement="bottom"
        arrow
      >
        <Button>
          <Space>
            {t("instances.menu.state", "State")}
            <DownOutlined />
          </Space>
        </Button>
      </Dropdown>
    </CanAccess>,
    <CanAccess key="menu-action" resource="instances" action="menuAction">
      <Dropdown
        menu={{ items: actionsMenu, onClick: onClickAction }}
        placement="bottom"
        arrow
      >
        <Button>
          <Space>
            {t("instances.menu.actions", "Actions")}
            <DownOutlined />
          </Space>
        </Button>
      </Dropdown>
    </CanAccess>,
  ];
  if (rootState.tenantMode) {
    buttons.push(
      <CanAccess key="menu-launch" resource="instances" action="menuLaunch">
        <Dropdown
          menu={{ items: launchMenu, onClick: onClickLaunch }}
          placement="bottom"
          arrow
        >
          <Button icon={<PlusCircleOutlined />}>
            <Space>
              {t("instances.menu.launch", "Launch Instance")}
              <DownOutlined />
            </Space>
          </Button>
        </Dropdown>
      </CanAccess>,
    );
  }
  buttons.push(
    <RefreshButton
      key="menu-refresh"
      onClick={() => {
        invalidate({
          resource: RESOURCE,
          invalidates: ["list"],
        });
      }}
    />,
  );
  return <div>
    <Space>{buttons}</Space>
    <Modal
      title={`Progress`}
      centered
      footer={
        <Button type="primary" onClick={onCloseStreaming}>
          Close
        </Button>
      }
      open={isOpen}
      onCancel={onCloseStreaming}
      maskClosable={false}
      width={800}
      height={600}
      resize="both"
      loading={loadingLog}
    >
      <TextArea value={ongoingLog} readOnly rows="25"
        style={{resize:"none", width:"100%",height:"100%"}}
        ref={textRef}/>
    </Modal>
  </div>;
};

export const InstanceDetailTabs = () => {
  let tabs = [
    {
      key: "details",
      label: "Details",
      children: <InstanceDetailPanel resource={RESOURCE} />,
    },
    { key: "audit", label: "Audit", children: <InstanceAuditPanel /> },
    { key: "security", label: "Security", children: <InstanceSecurityPanel /> },
    { key: "topology", label: "Topology", children: <InstanceTopologyPanel /> },
    { key: "data", label: "Data", children: <InstanceDataPanel /> },
    { key: "addons", label: "Add-ons", children: <InstanceAddonsPanel /> },
    { key: "monitoring", label: "Monitoring", children: <MonitoringPanel mode={"instance"}/>}
  ]
  //const { data: allowAccess } = useCan({action:"access", resource:"instances"});
  //if(allowAccess && allowAccess.can){
    tabs.push({ key: "access", label: "Access", children: <AccessPanel />});
    tabs.push({ key: "events", label: "Events", children: <EventsPanel type="instance"/>});
    tabs.push({ key: "maintenances", label: "Maintenances", children: <MaintenancesPanel type="instance"/> })
  //}

  return tabs;
};
